import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/cn';

/**
 * Menu suspenso acessível.
 *
 * Existe como componente porque o cabeçalho tem dois (tema e usuário) e as
 * partes chatas — devolver o foco ao gatilho, fechar no Escape, andar com as
 * setas — são justamente as que se esquece de repetir na segunda vez.
 *
 * Não usa `<dialog>` nem portal: o menu é curto, ancorado ao gatilho, e um
 * portal só acrescentaria o problema de reposicionar no scroll.
 */

interface MenuProps {
  /** Rótulo acessível do gatilho. */
  label: string;
  trigger: ReactNode;
  align?: 'start' | 'end';
  triggerClassName?: string;
  className?: string;
  /** `fechar(false)` para não devolver o foco ao gatilho — útil quando a ação
   *  desmonta a tela inteira e o gatilho deixa de existir. */
  children: (fechar: (devolverFoco?: boolean) => void) => ReactNode;
}

export function Menu({
  label,
  trigger,
  align = 'end',
  triggerClassName,
  className,
  children,
}: MenuProps) {
  const [aberto, setAberto] = useState(false);
  const raizRef = useRef<HTMLDivElement>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  const fechar = useCallback(
    (devolverFoco = true) => {
      setAberto(false);
      if (devolverFoco) gatilhoRef.current?.focus();
    },
    [],
  );

  useEffect(() => {
    if (!aberto) return;

    const aoClicarFora = (event: MouseEvent) => {
      if (!raizRef.current?.contains(event.target as Node)) setAberto(false);
    };
    // `pointerdown` e não `click`: fechar só no clique deixaria o menu aberto
    // durante o arrasto, e um segundo menu abriria por cima do primeiro.
    document.addEventListener('pointerdown', aoClicarFora);
    return () => document.removeEventListener('pointerdown', aoClicarFora);
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const primeiro = raizRef.current?.querySelector<HTMLElement>('[role^="menuitem"]');
    primeiro?.focus();
  }, [aberto]);

  function aoTeclar(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      fechar();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const itens = [...(raizRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])];
    if (itens.length === 0) return;
    event.preventDefault();
    const atual = itens.indexOf(document.activeElement as HTMLElement);
    const passo = event.key === 'ArrowDown' ? 1 : itens.length - 1;
    const proximo = itens[(Math.max(atual, 0) + passo) % itens.length];
    proximo?.focus();
  }

  return (
    <div ref={raizRef} className="relative" onKeyDown={aoTeclar}>
      <button
        ref={gatilhoRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((valor) => !valor)}
        className={cn(
          'inline-flex items-center gap-2 rounded-md text-sm transition-colors',
          'focus-visible:outline-none focus-visible:shadow-glow',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {aberto ? (
        <div
          id={id}
          role="menu"
          aria-label={label}
          className={cn(
            'absolute top-[calc(100%+0.375rem)] z-50 min-w-56 animate-fade-in',
            'rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-elevation',
            align === 'end' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {children(fechar)}
        </div>
      ) : null}
    </div>
  );
}

interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  /** Marca o item escolhido num grupo de opções mutuamente exclusivas. */
  selected?: boolean;
  tone?: 'default' | 'danger';
}

export function MenuItem({
  icon,
  selected,
  tone = 'default',
  className,
  children,
  ...props
}: MenuItemProps) {
  return (
    <button
      {...props}
      type="button"
      // `aria-checked` só é válido em `menuitemradio`; num `menuitem` comum o
      // leitor de tela ignora — ou pior, anuncia estado onde não há escolha.
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      // O foco dentro do menu é gerenciado pelas setas; deixar cada item no
      // fluxo do Tab faria o Tab escapar item a item em vez de sair do menu.
      tabIndex={-1}
      aria-checked={selected}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm',
        'transition-colors focus-visible:outline-none',
        'hover:bg-accent focus:bg-accent',
        'disabled:pointer-events-none disabled:opacity-50',
        tone === 'danger' ? 'text-destructive' : 'text-foreground',
        className,
      )}
    >
      {icon ? <span className="flex-none text-muted-foreground">{icon}</span> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {selected ? <span aria-hidden className="flex-none text-primary">•</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-border" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}
